import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaShoppingCart, FaExchangeAlt, FaEye } from 'react-icons/fa';
import { useCart } from '../../context/CartContext';
import { useComparison } from '../../context/ComparisonContext';
import { useInventory } from '../../context/InventoryContext';
import { useCatalog } from '../../context/CatalogContext';
import { withCdn } from '../../utils/assetUrl';
import './ProductCard.css';

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  const { addToComparison, isInComparison, canAddToComparison } = useComparison();
  const { getStock, isLowStock } = useInventory();
  const { categories } = useCatalog();
  const navigate = useNavigate();

  const stockCount = getStock(product.id);
  const outOfStock = stockCount <= 0;
  const lowStock = isLowStock(product.id);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(price);
  };

  const resolveCategorySlug = () => {
    const categoryKey = String(product.category || '').toLowerCase();
    const match = categories.find((cat) =>
      String(cat.id).toLowerCase() === categoryKey || String(cat.slug).toLowerCase() === categoryKey
    );
    if (match?.slug) return match.slug;
    return String(product.category || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9&]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const categorySlug = resolveCategorySlug();
  const subcategorySlug = product.subcategory
    ? String(product.subcategory)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9&]+/g, '-')
      .replace(/^-+|-+$/g, '')
    : '';

  const handleAddToCart = (e) => {
    e.preventDefault();
    if (!outOfStock) {
      addToCart(product);
    }
  };

  const handleQuickView = () => {
    navigate(`/product/${product.id}`);
  };

  const handleCompare = (e) => {
    e.preventDefault();
    if (isInComparison(product.id)) {
      navigate('/compare');
    } else if (canAddToComparison()) {
      addToComparison(product);
      navigate('/compare');
    } else {
      alert('You can compare up to 4 products at a time. Please remove a product from comparison to add a new one.');
    }
  };

  return (
    <div className="product-card">
      {product.discount && (
        <span className="product-badge discount">-{product.discount}%</span>
      )}
      {outOfStock && (
        <span className="product-badge out-of-stock">Out of Stock</span>
      )}
      {!outOfStock && lowStock && (
        <span className="product-badge low-stock">Low Stock</span>
      )}
      
      <Link to={`/product/${product.id}`} className="product-image-wrapper">
        <img src={withCdn(product.image)} alt={product.name} className="product-image" loading="lazy" decoding="async" />
        <div className="product-overlay">
          <button className="overlay-btn" onClick={handleAddToCart} title="Add to Cart" disabled={outOfStock} aria-label={`Add ${product.name} to cart`}>
            <FaShoppingCart />
          </button>
          <button 
            className={`overlay-btn ${isInComparison(product.id) ? 'active' : ''}`}
            onClick={handleCompare}
            title="Compare"
            aria-label={isInComparison(product.id) ? `View comparison` : `Add ${product.name} to comparison`}
          >
            <FaExchangeAlt />
          </button>
          <button
            className="overlay-btn"
            onClick={handleQuickView}
            title="Quick View"
            aria-label={`View ${product.name}`}
          >
            <FaEye />
          </button>
        </div>
      </Link>

      <div className="product-info">
        <div className="product-category">
          <Link to={`/category/${categorySlug}`}>
            {product.categoryName || product.category.replace('-', ' ')}
          </Link>
          {product.subcategory && (
            <>
              , <Link to={`/shop?subcategory=${subcategorySlug || product.subcategory}`}>
                {product.subcategory.replace('-', ' ')}
              </Link>
            </>
          )}
        </div>

        <h3 className="product-name">
          <Link to={`/product/${product.id}`}>{product.name}</Link>
        </h3>

        <div className="product-price">
          <span className="current-price">{formatPrice(product.price)}</span>
          {product.originalPrice && (
            <span className="original-price">{formatPrice(product.originalPrice)}</span>
          )}
        </div>

        <button className="add-to-cart-btn" onClick={handleAddToCart} disabled={outOfStock} aria-label={`Add ${product.name} to cart`}>
          <FaShoppingCart /> {outOfStock ? 'Out of Stock' : 'Add to cart'}
        </button>
        
        <button 
          className={`compare-btn ${isInComparison(product.id) ? 'active' : ''}`}
          onClick={handleCompare}
        >
          {isInComparison(product.id) ? 'Remove from Compare' : 'Compare'}
        </button>
      </div>
    </div>
  );
};

export default ProductCard;
